import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Users, TrendingUp, Calendar, UserPlus, Phone, CheckCircle, 
  XCircle, FileText, Briefcase, Building2, LogOut, LayoutDashboard,
  List, Settings, Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  new: "#3b82f6",
  waitlist: "#f59e0b",
  contacted: "#8b5cf6",
  qualified: "#10b981",
  unsuitable: "#ef4444",
  contract_sent: "#f97316",
  hired: "#22c55e",
  duplicate: "#ec4899"
};

const STATUS_LABELS = {
  new: "Neu",
  waitlist: "Nicht erreicht",
  contacted: "Kann erst später",
  qualified: "Qualifiziert",
  unsuitable: "Ungeeignet",
  contract_sent: "Weitergeleitet an T2M",
  hired: "Eingestellt",
  duplicate: "Doppelt"
};

export const AdminDashboard = ({ authToken, onNavigate, projectId }) => {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const fetchStats = async () => {
    setIsLoading(true);
    try {
      const params = projectId ? `?project_id=${projectId}` : "";
      const response = await axios.get(`${API}/admin/dashboard${params}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!stats) return null;

  const pieData = Object.entries(stats.by_status)
    .filter(([_, value]) => value > 0)
    .map(([key, value]) => ({
      name: STATUS_LABELS[key],
      value,
      color: STATUS_COLORS[key]
    }));

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Heute</p>
                <p className="text-3xl font-bold text-slate-900">{stats.today}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <UserPlus className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Letzte 7 Tage</p>
                <p className="text-3xl font-bold text-slate-900">{stats.last_7_days}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Dieser Monat</p>
                <p className="text-3xl font-bold text-slate-900">{stats.this_month}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Gesamt</p>
                <p className="text-3xl font-bold text-slate-900">{stats.total}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Funnel Status Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {Object.entries(STATUS_LABELS).map(([key, label]) => (
          <Card 
            key={key} 
            className="bg-white border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => onNavigate('leads', { status: key })}
          >
            <CardContent className="p-4 text-center">
              <div 
                className="w-8 h-8 rounded-full mx-auto mb-2 flex items-center justify-center"
                style={{ backgroundColor: `${STATUS_COLORS[key]}20` }}
              >
                <span className="text-lg font-bold" style={{ color: STATUS_COLORS[key] }}>
                  {stats.by_status[key] || 0}
                </span>
              </div>
              <p className="text-xs text-slate-600 font-medium">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Leads Chart */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">Leads pro Tag (14 Tage)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.daily_leads}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#64748b" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#64748b" allowDecimals={false} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">Status-Verteilung</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center">
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-slate-500">Keine Daten verfügbar</p>
              )}
            </div>
            <div className="flex flex-wrap justify-center gap-3 mt-4">
              {pieData.map((entry) => (
                <div key={entry.name} className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-xs text-slate-600">{entry.name}: {entry.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">Schnellaktionen</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button 
              variant="outline" 
              className="rounded-lg"
              onClick={() => onNavigate('leads', { status: 'new' })}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Neue Leads ({stats.by_status.new || 0})
            </Button>
            <Button 
              variant="outline" 
              className="rounded-lg"
              onClick={() => onNavigate('leads', { status: 'contacted' })}
            >
              <Phone className="w-4 h-4 mr-2" />
              Zu kontaktieren
            </Button>
            <Button 
              variant="outline" 
              className="rounded-lg text-emerald-600 border-emerald-200 hover:bg-emerald-50"
              onClick={() => onNavigate('leads', { status: 'hired' })}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Eingestellt ({stats.by_status.hired || 0})
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
